/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Mon Aug 12 2024
 **/
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";

import { JotaiStore } from "@/frameworks/libraries/commons";

import { NotificationItem, NotificationItemPayload } from "./atom";
import useMydeskNotification from "./useMydeskNotification";
import useMydeskNotificationList, { getKeyByValue } from "./useMydeskNotificationList";
import { formatMydeskSerializedData } from "./utils";
import { useStompClient } from "@/frameworks/libraries";
import { AlarmLogMessageType, AlarmLogMessageValueType } from "./types";

type Props = {
	onError?: (error?: Error) => void;
};

export default function useMydeskNotificationManager({ onError }: Props) {
	const { atom } = useMydeskNotification();
	const normal = useMydeskNotificationList();

	const run = useCallback(async () => {
		try {
			await normal.refetch().then(({ data }) => {
				if (data) {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					data.data?.content.forEach((item: any) => {
						updateData(formatMydeskSerializedData(item));
					});
				}
			});
		} catch (error) {
			onError?.(error as Error);
		}
	}, []);

	const updateData = useAtomCallback(
		(_, set, args?: NotificationItem) => {
			set(atom.notification, (prev) => {
				const next = new Map(prev);
				if (args) {
					next.set(args.payload?.readKey || "", args);
				}
				return next;
			});
		},
		{
			store: JotaiStore
		}
	);

	const deleteData = useAtomCallback(
		async (_, set, args?: NotificationItemPayload) => {
			set(atom.notification, (prev) => {
				const next = new Map(prev);
				if (args) {
					next.delete(args?.readKey);
				}
				return next;
			});
		},
		{
			store: JotaiStore
		}
	);

	useStompClient(
		{
			onMessageReceived: function (message) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const _message = message as Message.Frame<AlarmLogMessageValueType, any, Message.Options>;
				if (getKeyByValue(AlarmLogMessageType, message.type as keyof typeof AlarmLogMessageType)) {
					updateData({
						id: _message.payload.readKey,
						createdTime: new Date().getTime(),
						read: false,
						type: _message.type,
						payload: _message.payload
					});
				}
			}
		},
		[]
	);

	return {
		run,
		deleteData
	};
}
